import { Button, Form, FormProps, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onCreateRole, onGetPermissions } from 'modules/roles/services/roles';

// models
import { ModifiedPermission } from 'modules/roles/models/modifiedPermission';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ModulesPermissionsComponent from 'modules/roles/components/modulesPermission/modulesPermission';
import { useTranslation } from 'react-i18next';

function CreateRolesPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [permissions, setPermissions] = useState<ModifiedPermission[]>([]);
	const [form] = Form.useForm();
	const selectedPermissions: number[] = [];

	type FieldType = {
		name: string;
	};

	useEffect(() => {
		getPermissions();
	}, []);

	const getPermissions = async () => {
		try {
			setIsLoading(true);
			const response = await onGetPermissions();
			const permissions = response.data.data.permissions;

			const result: any = [];

			// Function to find or create the permission set for each entity (e.g., profile, role)
			const findOrCreatePermission = (name: string) => {
				let existing = result.find((item: any) => item.name === name);
				if (!existing) {
					existing = {
						name,
						create: { id: null, value: null },
						update: { id: null, value: null },
						delete: { id: null, value: null },
						view: { id: null, value: null },
						key: result.length,
					};
					result.push(existing);
				}
				return existing;
			};

			// Iterate over the permissions array
			permissions.forEach((permission) => {
				// Split the permission name into parts (e.g., "profile_update" -> ["profile", "update"])
				const [entity, action, dummy, dummy2, sliderAction] = permission.name.split('_');

				// Find or create the permission set for the entity
				const permissionSet = findOrCreatePermission(entity);

				//check for wallet as it have only UPDATE action
				if (entity === 'wallet') {
					permissionSet.update = { id: permission.id, value: 0 };
					return;
				}

				// check for mobile slider
				if (entity === 'mobile') {
					if (sliderAction === 'create') permissionSet.create = { id: permission.id, value: 0 };
					if (sliderAction === 'update') permissionSet.update = { id: permission.id, value: 0 };
					if (sliderAction === 'delete') permissionSet.delete = { id: permission.id, value: 0 };
					if (sliderAction === 'view') permissionSet.view = { id: permission.id, value: 0 };
					return;
				}

				// check for setting category
				if (entity === 'setting' && action == 'category') {
					const data = {
						name: 'setting_category',
						create: { id: null, value: null },
						update: { id: null, value: null },
						delete: { id: null, value: null },
						view: { id: permission.id, value: 0 },
						key: result.length,
					};
					result.push(data);
				}

				// Set the appropriate action flag based on the action (create, update, delete, view)
				if (action === 'create') permissionSet.create = { id: permission.id, value: 0 };
				if (action === 'update') permissionSet.update = { id: permission.id, value: 0 };
				if (action === 'delete') permissionSet.delete = { id: permission.id, value: 0 };
				if (action === 'view') permissionSet.view = { id: permission.id, value: 0 };
			});

			setPermissions(result);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/roles');
		}
	};

	const onUpdatePermission = (id: number) => {
		const idIndex = selectedPermissions.findIndex((el) => el === id);
		if (idIndex >= 0) selectedPermissions.splice(idIndex, 1);
		else selectedPermissions.push(id);
	};

	const handleFinishFailed = () => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	const handleFinish: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);
			await onCreateRole(values.name, 'admins', selectedPermissions);
			NotificationService.success(t('COMMON.SUCCESS'), t('ROLES.ROLE_CREATED'));
			navigate('/roles');
		} catch (error: any) {
			setIsSubmitting(false);
			if (error.response.data.data.errors.name.includes('The Name has already been taken.'))
				NotificationService.error(t('COMMON.ERROR'), t('ROLES.ROLE_ALREADY_EXIST'));
			else NotificationService.error(t('COMMON.OPS'), t('ROLES.ROLE_CREATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('ROLES.ADD_NEW')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className="sectionContainer">
						<h5 style={{ marginBottom: 15 }}>{t('ROLES.ADMIN_ROLE_INF')}</h5>

						<Form
							form={form}
							id="createForm"
							name="login"
							initialValues={{ remember: true }}
							onFinish={handleFinish}
							onFinishFailed={handleFinishFailed}>
							<Form.Item name="name" rules={[{ required: true, message: t('ROLES.ROLE_NAME_REQUIRED') }]}>
								<Input placeholder={t('ROLES.ROLE_NAME')} type="text" size="large" />
							</Form.Item>
						</Form>
					</div>

					<div className="sectionContainer">
						<h5 style={{ marginBottom: 15 }}>{t('ROLES.ADMIN_ROLE')}</h5>
						<ModulesPermissionsComponent permissions={permissions} onPermissionChange={onUpdatePermission} />

						<Button
							size="large"
							type="primary"
							htmlType="submit"
							form="createForm"
							loading={isSubmitting}
							style={{ marginTop: 20, marginInlineStart: 'auto', display: 'flex' }}>
							{t('COMMON.CREATE')}
						</Button>
					</div>
				</>
			)}
		</>
	);
}

export default CreateRolesPage;
